/**
 * ServicesPage.jsx — Premium tenant services page (revamp).
 *
 * Full service menu with category chip filter, premium bento grid,
 * and embedded booking. Wrapped in TenantThemeProvider + TenantLayout.
 * Legacy preserved at /:garageSlug/legacy/services via ServicesPageLegacy.jsx.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Clock, Tag, ArrowRight, Search } from 'lucide-react';

import { TenantThemeProvider } from '../components/tenant/TenantThemeContext';
import TenantLayout from '../components/tenant/TenantLayout';
import TenantSEO from '../components/tenant/TenantSEO';
import TenantLoader from '../components/tenant/ui/TenantLoader';
import {
    GlassCard, TiltCard, RevealOnScroll, SectionHeader,
    AmbientBlob, GridBackdrop, MagneticButton
} from '../components/tenant/ui/primitives';
import { Booking } from '../components/tenant/sections/Action';
import { FinalCTA } from '../components/tenant/sections/Closing';
import { useLanguage } from '../contexts/LanguageContext';
import { formatCurrency } from '../lib/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
    { value: 'all', en: 'All', bn: 'সব' },
    { value: 'maintenance', en: 'Maintenance', bn: 'রক্ষণাবেক্ষণ' },
    { value: 'engine', en: 'Engine', bn: 'ইঞ্জিন' },
    { value: 'electrical', en: 'Electrical', bn: 'ইলেকট্রিক্যাল' },
    { value: 'tires_brakes', en: 'Tires & Brakes', bn: 'টায়ার ও ব্রেক' },
    { value: 'detailing', en: 'Detailing', bn: 'ডিটেইলিং' }
];

export default function ServicesPage() {
    const { garageSlug } = useParams();
    const { language } = useLanguage();
    const [tenant, setTenant] = useState(null);
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [activeCat, setActiveCat] = useState('all');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!garageSlug) return;
        let cancelled = false;
        const siteReq = axios.get(`${API}/tenants/${garageSlug}/site`)
            .catch(() => axios.get(`${API}/tenants/${garageSlug}/public`));
        const servicesReq = axios.get(`${API}/tenants/${garageSlug}/services`).catch(() => ({ data: [] }));
        const catsReq = axios.get(`${API}/tenants/${garageSlug}/categories/services`).catch(() => ({ data: [] }));

        Promise.all([siteReq, servicesReq, catsReq])
            .then(([t, s, c]) => {
                if (cancelled) return;
                setTenant(t.data);
                setServices(Array.isArray(s.data) ? s.data : []);
                if (Array.isArray(c.data) && c.data.length > 0) {
                    const seen = new Set(DEFAULT_CATEGORIES.map(x => x.value));
                    const extras = c.data
                        .filter(x => !seen.has(x.value))
                        .map(x => ({ value: x.value, en: x.label || x.value, bn: x.label_bn || x.label || x.value }));
                    setCategories([...DEFAULT_CATEGORIES, ...extras]);
                }
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 404) setNotFound(true);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [garageSlug]);

    const visibleCategories = useMemo(() => {
        const used = new Set(services.map(s => s.category).filter(Boolean));
        return categories.filter(c => c.value === 'all' || used.has(c.value));
    }, [categories, services]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return services.filter(s => {
            const catOk = activeCat === 'all' || s.category === activeCat;
            const qOk = !q || (s.name?.toLowerCase().includes(q) || s.name_bn?.includes(query) || s.description?.toLowerCase().includes(q));
            return catOk && qOk;
        });
    }, [services, activeCat, query]);

    if (loading) return <Loading slug={garageSlug} />;
    if (notFound || !tenant) return <NotFound slug={garageSlug} />;

    return (
        <TenantThemeProvider tenant={tenant}>
            <TenantSEO tenant={tenant} slug={garageSlug} page="services" services={services} />
            <TenantLayout>
                <ServicesHero count={services.length} tenant={tenant} />
                <ServicesFilterAndGrid
                    services={filtered}
                    categories={visibleCategories}
                    activeCat={activeCat}
                    onCat={setActiveCat}
                    query={query}
                    onQuery={setQuery}
                    language={language}
                />
                <Booking slug={garageSlug} services={services} />
                <FinalCTA />
            </TenantLayout>
        </TenantThemeProvider>
    );
}

function ServicesHero({ count, tenant }) {
    const { language } = useLanguage();
    return (
        <section className="relative pt-24 md:pt-28 pb-10 overflow-hidden">
            <AmbientBlob className="-top-32 -left-32" size={600} />
            <AmbientBlob className="-top-20 right-0" size={500} delay={0.3} />
            <GridBackdrop opacity={0.05} />
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <RevealOnScroll>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--tenant-border)] bg-[var(--tenant-surface)]/60 backdrop-blur text-xs uppercase tracking-[0.28em] text-[var(--tenant-muted)]">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--tenant-primary)]" />
                        {language === 'bn' ? 'সার্ভিস মেনু' : 'Service menu'}
                    </div>
                    <h1 className={['mt-6 font-display text-6xl md:text-8xl tracking-wider text-[var(--tenant-ink)]', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                        {language === 'bn' ? 'সব সার্ভিস' : 'Everything we fix.'}
                    </h1>
                    <p className="mt-4 text-lg text-[var(--tenant-muted)] max-w-2xl">
                        {language === 'bn'
                            ? `${count}+ ধরনের সার্ভিস — স্বচ্ছ মূল্য, প্রকৃত পার্টস, মাস্টার মেকানিকের হাতে।`
                            : `${count}+ services. Transparent pricing, genuine parts, master mechanics.`}
                    </p>
                </RevealOnScroll>
            </div>
        </section>
    );
}

function ServicesFilterAndGrid({ services, categories, activeCat, onCat, query, onQuery, language }) {
    return (
        <section className="relative pb-24" id="services-grid">
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <div className="sticky top-20 z-20 -mx-5 md:mx-0 px-5 md:px-0 py-4 backdrop-blur-md bg-[var(--tenant-surface)]/70 border-y border-[var(--tenant-border)]">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-2 px-3 h-11 rounded-full border border-[var(--tenant-border)] bg-[var(--tenant-surface)] md:w-72">
                            <Search className="w-4 h-4 text-[var(--tenant-muted)]" />
                            <input
                                type="search"
                                placeholder={language === 'bn' ? 'খুঁজুন…' : 'Search services…'}
                                value={query}
                                onChange={e => onQuery(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-sm text-[var(--tenant-ink)]"
                            />
                        </div>
                        <div className="flex-1 overflow-x-auto -mx-1 scrollbar-none">
                            <div className="inline-flex gap-2 px-1">
                                {categories.map(c => (
                                    <button
                                        key={c.value}
                                        onClick={() => onCat(c.value)}
                                        className={[
                                            'h-9 px-4 rounded-full text-xs uppercase tracking-[0.18em] font-semibold whitespace-nowrap transition border',
                                            activeCat === c.value
                                                ? 'bg-[var(--tenant-primary)] text-[var(--tenant-surface)] border-transparent'
                                                : 'border-[var(--tenant-border)] text-[var(--tenant-ink)] hover:border-[var(--tenant-primary)]'
                                        ].join(' ')}
                                    >
                                        {language === 'bn' ? c.bn : c.en}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {services.length === 0 ? (
                    <div className="mt-16 text-center text-[var(--tenant-muted)]">
                        {language === 'bn' ? 'কোনো সার্ভিস পাওয়া যায়নি।' : 'No services match your filter.'}
                    </div>
                ) : (
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {services.map((s, i) => (
                            <RevealOnScroll key={s.id} delay={Math.min(i * 0.03, 0.3)}>
                                <TiltCard max={4}>
                                    <GlassCard className="p-6 h-full flex flex-col">
                                        {s.category && (
                                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] text-[10px] uppercase tracking-[0.18em] self-start">
                                                <Tag className="w-3 h-3" />
                                                {s.category.replace(/_/g, ' ')}
                                            </div>
                                        )}
                                        <h3 className={['mt-3 font-display text-2xl tracking-wide text-[var(--tenant-ink)]', language === 'bn' && s.name_bn ? 'font-bn' : ''].join(' ')}>
                                            {language === 'bn' && s.name_bn ? s.name_bn : s.name}
                                        </h3>
                                        {s.description && (
                                            <p className="mt-2 text-sm text-[var(--tenant-muted)] leading-relaxed line-clamp-3">
                                                {s.description}
                                            </p>
                                        )}
                                        <div className="mt-auto pt-5 flex items-end justify-between gap-4">
                                            <div>
                                                {s.estimated_time && (
                                                    <div className="inline-flex items-center gap-1.5 text-xs text-[var(--tenant-muted)]">
                                                        <Clock className="w-3.5 h-3.5" /> {s.estimated_time}
                                                    </div>
                                                )}
                                                {s.price > 0 && (
                                                    <div className="mt-1 font-display text-2xl text-[var(--tenant-primary)] tracking-wider">
                                                        {formatCurrency(s.price, language)}
                                                    </div>
                                                )}
                                            </div>
                                            <a
                                                href="#booking"
                                                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] font-semibold text-[var(--tenant-primary)] hover:gap-2 transition-all"
                                            >
                                                {language === 'bn' ? 'বুক করুন' : 'Book'}
                                                <ArrowRight className="w-3.5 h-3.5" />
                                            </a>
                                        </div>
                                    </GlassCard>
                                </TiltCard>
                            </RevealOnScroll>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function Loading({ slug }) {
    return <TenantLoader slug={slug} label="Services" />;
}

function NotFound({ slug }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8f6f0] text-[#0c1a12] px-6">
            <div className="text-center max-w-md">
                <AlertTriangle className="w-12 h-12 mx-auto text-amber-600 mb-4" />
                <h1 className="font-display text-4xl tracking-wider">Workshop not found</h1>
                <p className="mt-3 text-[#0c1a12]/60 text-sm">No garage at <span className="font-mono">/{slug}</span>.</p>
                <Link to="/" className="inline-block mt-6 px-5 py-2 rounded-full bg-[#0c1a12] text-white font-semibold text-sm uppercase tracking-wide hover:brightness-110">Home</Link>
            </div>
        </div>
    );
}
